import React, { useState, useEffect, useContext } from "react";
import { Box, Grid, Hidden, useTheme } from "@mui/material";
import { DropdownSelect } from "../../../components/DropdownSelect";
import coursePic from "../../../assets/images/course_pic.png";
import CourseCard from "../../../components/CourseCard/CourseCard";
import DisplayOptionsButtons from "../../../components/Buttons/DisplayOptionsButtons";
import PoliciesFilter from "./PoliciesFilter";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar
} from "../../../redux/actions/snackbarActions";
import {
  PaginationHeader,
  PaginationFooter
} from "../../../components/Pagination";
import UserAssignmentContext from "../../../context/UserAssignmentContext";
import CourseActionButton from "../../../components/Buttons/CourseActionButton";
import { assignmentTypes, courseTypes } from "../../../constants";

const gridView = "grid";
const listView = "list";

const CoursesList = ({
  onPerPageChange,
  onPageChange,
  courses,
  sortByOptions,
  sortByValue,
  onSortOptionChange,
  page,
  perPage,
  perPageOptions,
  totalCount,
  onFilterPackage,
  packageFilterValue,
  packageFilterOptions,
  courseType,
  refetchCourses
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [displayCoursesAsGrid, setDisplayCoursesAsGrid] = useState(true);
  const theme = useTheme();
  const {
    onStartCourse,
    isStarted,
    onCreateUserAssignment,
    onResumeAssignment,
    isEnrolled,
    needsRefetch,
    setNeedsRefetch
  } = useContext(UserAssignmentContext);

  const handleResize = event => {
    if (event.currentTarget.innerWidth < theme.breakpoints.values.md) {
      setDisplayCoursesAsGrid(true);
    }
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (needsRefetch) {
      if (refetchCourses) {
        refetchCourses();
        setNeedsRefetch(false);
      }
    }
  }, [needsRefetch]);

  const handleAddToCoursesClick = course => {
    const userAssignment = course.userAssignment;
    if (!userAssignment) {
      return onCreateUserAssignment(course).then(response => {
        if (course.courseType === courseTypes.policy) {
          if (response.errors) {
            dispatch(
              enqueueErrorSnackbar({
                message: t("libraryAllPolicies.savePolicyErrorMessage")
              })
            );
          } else {
            dispatch(
              enqueueSuccessSnackbar({
                message: t("libraryAllPolicies.savePolicySuccessMessage")
              })
            );
          }
        }
      });
    }
  };

  const handleDisplayChange = newDisplayOption => {
    setDisplayCoursesAsGrid(newDisplayOption === gridView);
  };

  const handlePerPageChange = perPage => {
    onPerPageChange(perPage);
  };

  const handlePageChange = page => {
    onPageChange(page);
  };

  const courseContainerSize = {
    xs: 12,
    sm: displayCoursesAsGrid ? 6 : 12,
    md: displayCoursesAsGrid ? 3 : 12,
    xl: displayCoursesAsGrid ? 2 : 12
  };

  return (
    <Box data-qa="library-courses-list" display="flex" flexDirection="column">
      {courseType == courseTypes.policy.toLowerCase() && (
        <PoliciesFilter
          onFilterPackage={onFilterPackage}
          packageFilterValue={packageFilterValue}
          packageFilterOptions={packageFilterOptions}
        />
      )}
      <Box
        display="flex"
        mb={{ xs: 3, sm: 2 }}
        mt={{ xs: 1, lg: 0 }}
        alignItems="center"
        width="100%"
        justifyContent={{ xs: "flex-end", md: "space-between" }}
      >
        <PaginationHeader
          numPerPageOptions={perPageOptions}
          numItems={totalCount}
          numItemsPerPage={perPage}
          currentPage={page}
          itemsType="Assignments"
          onPerPageChange={handlePerPageChange}
          showTotalResults={false}
        />
        <Box
          data-qa="library-courses-sort-options"
          display="flex"
          alignItems="center"
        >
          <Hidden mdDown>
            <Box mr={2}>
              <DisplayOptionsButtons
                onDisplayChange={handleDisplayChange}
                currentDisplayOption={
                  displayCoursesAsGrid ? gridView : listView
                }
              />
            </Box>
          </Hidden>
          {sortByOptions && sortByValue && (
            <DropdownSelect
              options={sortByOptions}
              value={sortByValue}
              onChange={onSortOptionChange}
              label="Sort By"
            />
          )}
        </Box>
      </Box>
      <Grid container spacing={3}>
        {courses.map((course, index) => {
          const userAssignment = course.userAssignment;
          return (
            <Grid item {...courseContainerSize} key={index}>
              <CourseCard
                location={
                  course.iltInfo ? course.iltInfo.trainingLocation : null
                }
                gridView={displayCoursesAsGrid}
                flags={course.flags}
                title={course.name}
                manualName={course.manualName}
                duration={course.duration}
                number={course.number}
                hasNote={!!userAssignment}
                dueDate={
                  userAssignment?.assignmentType !== assignmentTypes.self &&
                  userAssignment?.dueDate
                }
                isSelfAssigned={
                  userAssignment?.assignmentType == assignmentTypes.self
                }
                buttons={[
                  <CourseActionButton
                    isEnrolled={isEnrolled(course)}
                    startDate={course.zonedStartDate}
                    endDate={course.zonedEndDate}
                    isAssigned={!!userAssignment}
                    isStarted={isStarted(userAssignment)}
                    courseType={course.courseType}
                    onEnrollClick={() => onStartCourse(course)}
                    onResumeCourseClick={() =>
                      onResumeAssignment(userAssignment)
                    }
                    onAddToCoursesClick={() => handleAddToCoursesClick(course)}
                    isInstructorForCourse={course.isInstructorForCourse}
                    prereqs={course.prerequisites || []}
                    hasNoFreeSeats={course.noFreeSeats}
                    iltClassType={course.typeName}
                  />
                ]}
                courseType={course.typeName}
                typeName={course.typeName}
                scheduledDate={course.scheduledDate}
                scheduledTime={course.scheduledTime}
                imageSrc={course.image || coursePic}
                courseDescription={course.description}
              />
            </Grid>
          );
        })}
      </Grid>
      <Box mt={5}>
        <PaginationFooter
          numPerPageOptions={perPageOptions}
          numItems={totalCount}
          numItemsPerPage={perPage}
          currentPage={page}
          onPerPageChange={handlePerPageChange}
          onPageChange={handlePageChange}
        />
      </Box>
    </Box>
  );
};

export default CoursesList;
